import React, { FunctionComponent, useEffect } from 'react';
import { useRouteMatch } from 'react-router';
import { Radio } from 'antd';
import { useStore } from 'effector-react';
import { RadioChangeEvent } from 'antd/lib/radio';
import { ExclamationCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import routes from '@config/routes';
import componentContent from './SpamSelector.content.json';
import useTranslate from '@hooks/useTranslate';
import { goTo } from '@helpers/navigation';
import spamsUnread, { bootstrapUnreadSpams } from '@stores/spamsUnread';
import css from './SpamSelector.module.less';

const { spam, spamComments, spamUsers, spamStatuses } = routes;
const spamRoutes = [spam, spamComments, spamUsers, spamStatuses];

interface SpamSelectorProps {}

const SpamSelector: FunctionComponent<SpamSelectorProps> = () => {
  const match = useRouteMatch();
  const currentRoute = spamRoutes.find((route) => route.path === match.path);
  const content = useTranslate(componentContent);
  const onChange = ({ target }: RadioChangeEvent) => goTo(target.value);
  const spamsUnreadStore = useStore(spamsUnread);

  useEffect(() => {
    let timerId: number | null = null;
    let idleId: number | null = null;

    const runBootstrap = () => {
      bootstrapUnreadSpams();
    };

    if ('requestIdleCallback' in window) {
      idleId = (window as any).requestIdleCallback(runBootstrap, { timeout: 500 });
    } else {
      timerId = window.setTimeout(runBootstrap, 200);
    }

    return () => {
      if (timerId) {
        window.clearTimeout(timerId);
      }

      if (idleId && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleId);
      }
    };
  }, []);

  const getExclamationIcon = (routeName: string) => {
    const isIconDisplayed =
      spamsUnreadStore[routeName as 'spam' | 'spamUsers' | 'spamComments' | 'spamStatuses'];

    return (
      isIconDisplayed && (
        <div className={css.icon}>
          {currentRoute?.name === routeName ? (
            <ExclamationCircleFilled />
          ) : (
            <ExclamationCircleOutlined />
          )}
        </div>
      )
    );
  };

  return (
    <Radio.Group
      className={css.group}
      onChange={onChange}
      value={currentRoute ? currentRoute.name : undefined}
    >
      {spamRoutes.map((route) => (
        <Radio.Button
          key={route.name}
          value={route.name}
          className={css.button}
        >
          {getExclamationIcon(route.name)}
          {content[route.name]}
        </Radio.Button>
      ))}
    </Radio.Group>
  );
};

export default SpamSelector;
